import { FixedDimensionOrderKey } from '@sit/core';
import { DimensionOrder, useGetTimesheet } from '@sit/openapi';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { CompanyDimensionFlags } from '@web/helpers/timesheet-header';
import keyBy from 'lodash/keyBy';

export const LEGACY_DIMENSIONS_FLAGS = {
  ALLOCATION: 'allocationsEnabled',
  CUSTOMER: 'showCustom',
  PROJECT: 'showProject',
  TASK: 'showTask',
  ITEM: 'showItem',
  COSTTYPE: 'showCostType',
  TIMETYPE: 'showTimeType',
  EMPLOYEEPOSITION: 'showEmployeePosition',
  LABORCLASS: 'showLaborClass',
  LABORSHIFT: 'showLaborShift',
  LABORUNION: 'showLaborUnion',
  BILLABLE: 'showBillable',
  DEPARTMENT: 'showDepartmentLocation',
  LOCATION: 'showDepartmentLocation',
} satisfies Record<FixedDimensionOrderKey, keyof CompanyDimensionFlags>;
export const AcceptableObjectNameKeys = Object.keys(LEGACY_DIMENSIONS_FLAGS) as AcceptableObjectName[];

export type AcceptableObjectName = keyof typeof LEGACY_DIMENSIONS_FLAGS;
export type LegacyDimensionsFlags = {
  [K in AcceptableObjectName as (typeof LEGACY_DIMENSIONS_FLAGS)[K]]: boolean;
};

function toLegacyDimensionsFlags(dimensionsOrder: DimensionOrder[]) {
  const dimensionsMap = keyBy(dimensionsOrder, 'objectName');

  return AcceptableObjectNameKeys.reduce((flags, key) => {
    flags[LEGACY_DIMENSIONS_FLAGS[key]] = !!dimensionsMap[key];
    return flags;
  }, {} as LegacyDimensionsFlags);
}

/**
 * @param timesheetId `undefined` must only used when creating demo activity cards
 */
export function useEnabledTimesheetDimensions<TData = [DimensionOrder[], LegacyDimensionsFlags], TOut = TData>(
  timesheetId: string | undefined,
  select?: (data: TData) => TOut,
): TOut {
  const { data: timesheetDimensionsOrder = [] } = useGetTimesheet(timesheetId, {
    select: (data) => data.dimensionsOrder,
  });

  const { data: companyDimensionsOrder = [] } = useGetCurrentCompany({
    select: (data) => data.dimensionsOrder,
  });

  const enabledDimensionsOrder = timesheetDimensionsOrder ?? companyDimensionsOrder ?? [];

  const result = [enabledDimensionsOrder, toLegacyDimensionsFlags(enabledDimensionsOrder)] as TData;

  const selectFunction = select ?? ((data) => data as unknown as TOut);
  return selectFunction(result);
}
